import { SHOW_REACTIONS } from "@app/constants";
import { getUserToken } from "@app/utils/deviceId";
import type { ReactionProp } from "@src/app/types/Reactions";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  addReaction,
  deleteReaction,
  fetchReactions,
  fetchUserReaction,
} from "./reactionsApi";

export type ArticleReactionsState = {
  reactions: ReactionProp[];
  userReaction: string; // reaction id, or "0" when the user hasn't reacted
  loading: boolean;
  isSubmitting: boolean;
  error: boolean;
  totalReactions: number;
  toggleReaction: (reactId: string) => Promise<void>;
};

/**
 * Loads reaction counts + the current user's reaction for a single article and
 * exposes an optimistic toggle. Calls the reaction service directly (CORS-open,
 * keyless). Scoped to one widget instance, so no global store is needed.
 */
export function useArticleReactions(articleId?: string): ArticleReactionsState {
  const [reactions, setReactions] = useState<ReactionProp[]>([]);
  const [userReaction, setUserReaction] = useState("0");
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(false);

  // Guards against state updates after unmount / articleId change.
  const activeArticleRef = useRef<string | undefined>(articleId);

  useEffect(() => {
    activeArticleRef.current = articleId;
    if (!articleId || !SHOW_REACTIONS) return;

    const controller = new AbortController();
    const { signal } = controller;
    const userToken = getUserToken();

    // Drop results if the effect was cleaned up or the article changed. A
    // function call avoids TS narrowing `signal.aborted` to false across awaits.
    const isStale = () =>
      signal.aborted || activeArticleRef.current !== articleId;

    const load = async () => {
      setLoading(true);
      setError(false);
      try {
        const base = await fetchReactions(articleId);

        if (isStale()) return;

        // Unregistered articles return { message } with no reactions array —
        // surface as "no reactions" so the widget hides, not an error.
        if (!Array.isArray(base.reactions) || base.reactions.length === 0) {
          setReactions([]);
          setUserReaction("0");
          setLoading(false);
          return;
        }

        setReactions(base.reactions);

        let userReactId = "0";
        if (userToken.id) {
          try {
            const user = await fetchUserReaction(articleId, userToken);
            userReactId = user.react || "0";
          } catch {
            /* keep default "0" */
          }
        }

        if (isStale()) return;
        setUserReaction(userReactId);
        setLoading(false);
      } catch {
        if (isStale()) return;
        setError(true);
        setLoading(false);
      }
    };

    void load();
    return () => {
      controller.abort();
    };
  }, [articleId]);

  const toggleReaction = useCallback(
    async (reactId: string) => {
      if (!articleId || isSubmitting || !SHOW_REACTIONS) return;

      const userToken = getUserToken();
      if (!userToken.id) return;

      const prevReactId = userReaction;
      // Tapping the currently-selected reaction removes it.
      const nextReactId =
        prevReactId !== "0" && prevReactId === reactId ? "0" : reactId;
      const prevReactions = reactions;

      // Optimistic count update.
      setReactions((current) =>
        current.map((r) => {
          if (r.id === nextReactId && nextReactId !== "0")
            return { ...r, count: r.count + 1 };
          if (r.id === prevReactId && prevReactId !== "0")
            return { ...r, count: r.count - 1 };
          return r;
        }),
      );
      setUserReaction(nextReactId);
      setIsSubmitting(true);

      try {
        const res =
          nextReactId !== "0"
            ? await addReaction(articleId, nextReactId, userToken)
            : await deleteReaction(articleId, userToken);

        if (!res.success) throw new Error(res.message || "Reaction failed");

        if (res.data?.reactions && activeArticleRef.current === articleId) {
          setReactions(res.data.reactions);
        }
      } catch {
        if (activeArticleRef.current === articleId) {
          setReactions(prevReactions);
          setUserReaction(prevReactId);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [articleId, isSubmitting, reactions, userReaction],
  );

  const totalReactions = reactions.reduce((sum, r) => sum + r.count, 0);

  return {
    reactions,
    userReaction,
    loading,
    isSubmitting,
    error,
    totalReactions,
    toggleReaction,
  };
}
